import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  Clock, 
  DollarSign, 
  FolderOpen,
  Calendar,
  Target,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';
import ProjectDetailsModal from './ProjectDetailsModal';
import ProjectCard from './ProjectCard';
import { getDeadlineSortPriority } from '../lib/dateHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ProjectDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = async (project) => {
    try {
      // Fetch fresh project data to ensure hours_this_week is up-to-date
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/projects/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(response.data);
      setShowProjectModal(true);
    } catch (error) {
      console.error('Error fetching project details:', error);
      // Fallback to using project from list if fetch fails
      setSelectedProject(project);
      setShowProjectModal(true);
    }
  };

  const metrics = dashboardData?.metrics;
  const insights = dashboardData?.insights;
  const rawProjects = dashboardData?.projects ?? [];

  const sortedProjects = useMemo(() => {
    return [...rawProjects].sort(
      (a, b) => getDeadlineSortPriority(a) - getDeadlineSortPriority(b)
    );
  }, [rawProjects]);
  const totalProjectsCount = dashboardData?.total_projects ?? sortedProjects.length;


  const hasBudgetedProjects = sortedProjects.some(
    (project) =>
      project?.budget &&
      (Number(project.budget.time_hours) > 0 || Number(project.budget.money_amount) > 0)
  );

  if (loading) {
    return (
      <div className="mb-6">
        <div className="text-center py-8">
          <div className="inline-block w-10 h-10 border-4 border-cyan-500 dark:border-cyan-400 border-t-transparent rounded-full animate-spin transition-colors"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-3 text-sm transition-colors">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData || !metrics || !insights) {
    return null;
  }

  return (
    <div className="mb-6 space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Hours */}
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
                <Clock className="w-5 h-5 text-white" />
              </div>
              {metrics.comparison_last_week.trend !== 'same' && (
                <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full transition-colors ${
                  metrics.comparison_last_week.trend === 'up' 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                }`}>
                  {metrics.comparison_last_week.trend === 'up' ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  <span>{Math.abs(metrics.comparison_last_week.percent_change)}%</span>
                </div>
              )}
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1 transition-colors">
              {metrics.total_hours_this_week}h
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 transition-colors">Hours This Week</div>
          </div>
        </Card>

        {/* Total Earned */}
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1 transition-colors">
              ${metrics.total_earned.toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 transition-colors">Earned This Week</div>
          </div>
        </Card>

        {/* Active Projects */}
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1 transition-colors">
              {metrics.active_projects}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 transition-colors">Active Projects</div>
          </div>
        </Card>

        {/* Projects Status */}
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                metrics.projects_at_risk > 0
                  ? 'bg-gradient-to-br from-orange-500 to-red-500'
                  : 'bg-gradient-to-br from-green-500 to-emerald-500'
              }`}>
                {metrics.projects_at_risk > 0 ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1 transition-colors">
              {metrics.projects_at_risk > 0 ? metrics.projects_at_risk : insights.on_track_count}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 transition-colors">
              {metrics.projects_at_risk > 0 ? 'Need Attention' : 'On Track'}
            </div>
          </div>
        </Card>
      </div>

      {/* Weekly Insights */}
      {(insights.most_productive || insights.needs_attention.length > 0) && (
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg transition-colors">
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-cyan-500 dark:text-cyan-400" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 transition-colors">Weekly Insights</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Most Productive */}
              {insights.most_productive && (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-cyan-200 dark:border-cyan-800 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <div className="text-xs font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wide transition-colors">
                      Most Productive
                    </div>
                  </div>
                  <div className="font-semibold text-gray-800 dark:text-gray-100 transition-colors">{insights.most_productive.name}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">{insights.most_productive.hours}h this week</div>
                </div>
              )}

              {/* Needs Attention */}
              {insights.needs_attention.length > 0 && (
                <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <div className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide transition-colors">
                      Needs Attention
                    </div>
                  </div>
                  <div className="space-y-2">
                    {insights.needs_attention.map((item, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-semibold text-gray-800 dark:text-gray-100 transition-colors">{item.name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">{item.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Project Cards */}
      {sortedProjects.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 transition-colors">Project Summary</h3>
            {totalProjectsCount > sortedProjects.length && (
              <button
                onClick={() => navigate('/projects')}
                className="text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium transition-colors"
              >
                View All ({totalProjectsCount})
              </button>
            )}
          </div>

          {hasBudgetedProjects ? (
            <div className="space-y-4">
              {sortedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={{
                    ...project,
                    spent: project.spent || { time_hours: 0, money_amount: 0 }
                  }}
                  onClick={() => handleProjectClick(project)}
                  onDeadlineAction={() => navigate(`/projects?edit=${project.id}&focus=timeline`)}
                  onBudgetAction={() => navigate(`/projects?edit=${project.id}&focus=budget`)}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sortedProjects.map((project) => (
                <Card
                  key={project.id}
                  className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-0 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: project.color }}
                        />
                        <h4 className="font-semibold text-gray-800 dark:text-gray-100 truncate transition-colors">{project.name}</h4>
                        {project.is_default && (
                          <span className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 px-2 py-0.5 rounded-full transition-colors">
                            Default
                          </span>
                        )}
                      </div>
                      <div
                        className="px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ml-2 transition-colors"
                        style={{
                          backgroundColor: project.status.bgColor,
                          color:
                            project.status.color === 'green'
                              ? '#047857'
                              : project.status.color === 'yellow'
                                ? '#92400e'
                                : project.status.color === 'orange'
                                  ? '#9a3412'
                                  : '#991b1b'
                        }}
                      >
                        {project.status.label}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                      Track hours to see budget progress for this project.{' '}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/projects?edit=${project.id}&focus=budget`);
                        }}
                        className="underline text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 rounded transition-colors"
                      >
                        Set budget
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No Projects State */}
      {sortedProjects.length === 0 && (
        <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
          <div className="p-8 text-center">
            <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No active projects yet</p>
            <button 
              onClick={() => navigate('/projects')}
              className="mt-3 text-cyan-600 hover:text-cyan-700 font-medium text-sm"
            >
              Create your first project
            </button>
          </div>
        </Card>
      )}

      {/* Project Details Modal */}
      <ProjectDetailsModal
        open={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        project={selectedProject}
      />
    </div>
  );
};

export default ProjectDashboard;
